import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { ApprovalConfig as ApprovalConfigApi, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany } from "../../../../contexts";
import { useLanguage } from "../../../../contexts";
import { isMaster, isAdmin } from "../../../../utils/checkPermission";
import "./ApprovalConfig.scss";

const api            = new ApprovalConfigApi();
const siteController = new Site();

// ── Constantes (valores para BD, labels se traducen en render) ───────────────
const CRITERIO_OPTIONS = [
  { value: "auto",      tkey: "apc_criterio_auto"      },
  { value: "rol",       tkey: "apc_criterio_rol"       },
  { value: "usuario",   tkey: "apc_criterio_usuario"   },
  { value: "jerarquia", tkey: "apc_criterio_jerarquia" },
];

const ROL_OPTIONS = [
  "Supervisor del sector",
  "Responsable EHS / SHE Coordinator",
  "Gerente del sitio",
  "Jefe de Operaciones",
  "Ingeniero de seguridad",
  "Analista SHE",
];

const ACCIONES = [
  { key: "aprobar",             tkey: "flujo_accion_aprobar"            },
  { key: "aprobar_condiciones", tkey: "flujo_accion_aprobar_condiciones"},
  { key: "rechazar",            tkey: "flujo_accion_rechazar"           },
  { key: "suspender",           tkey: "flujo_accion_suspender"          },
  { key: "solicitar_cambios",   tkey: "flujo_accion_solicitar_cambios"  },
];

const CONDICION_OPTIONS = [
  { value: "Tipo: Espacio confinado Clase A",             tkey: "apc_cond_confined_a"  },
  { value: "Tipo: Eléctrico MT/AT (≥ 1 kV)",             tkey: "apc_cond_elec_mt"     },
  { value: "Tipo: Izaje crítico (carga > 75% capacidad)", tkey: "apc_cond_lifting"     },
  { value: "Tipo: Trabajo en caliente con llama abierta", tkey: "apc_cond_hot_work"    },
  { value: "Tipo: Demolición / Derribo",                  tkey: "apc_cond_demolition"  },
  { value: "Ejecutora: Contratista",                      tkey: "apc_cond_contractor"  },
  { value: "Riesgo residual ≥ 15 (extremo)",              tkey: "apc_cond_residual"    },
];

const ACCION_REGLA_OPTIONS = [
  { value: "agregar_nivel", tkey: "apc_accion_agregar_nivel" },
  { value: "notificar",     tkey: "apc_accion_notificar"     },
];

const ESCALAR_A_OPTIONS = [
  { value: "jefe_directo",  tkey: "flujo_escalar_jefe_directo"  },
  { value: "gerente_sitio", tkey: "flujo_escalar_gerente_sitio" },
  { value: "ehs_corporate", tkey: "flujo_escalar_ehs_corporate" },
];

const ACCION_VENCER_OPTIONS = [
  { value: "cambiar_estado_vencido", tkey: "apc_accion_cambiar_vencido"   },
  { value: "suspender",              tkey: "apc_accion_suspender_permiso" },
  { value: "notificar_solo",         tkey: "apc_accion_solo_notificar"    },
];

const SUSP_AUTO_OPTIONS = [
  { key: "gasesEspacioConfinado", tkey: "apc_susp_gases"    },
  { key: "vientosIzaje",          tkey: "apc_susp_winds"    },
  { key: "accidenteSitio",        tkey: "apc_susp_accident" },
  { key: "eppMalEstado",          tkey: "apc_susp_epp"      },
  { key: "alarmaIncendio",        tkey: "apc_susp_fire"     },
  { key: "capacitacionVence",     tkey: "apc_susp_training" },
];

// ── Helper ────────────────────────────────────────────────────────────────────
const emptyTitular   = () => ({ nombre: "", email: "", cargo: "", firmaDigital: false });
const emptySuplente  = () => ({ nombre: "", email: "", cargo: "" });
const emptyRegla     = () => ({ condicion: "", accion: "agregar_nivel", aprobadorAdicional: "", activa: true });
const emptyDeleg     = () => ({ delegante: "", delegado: "", niveles: [], desde: "", hasta: "", motivo: "" });

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-AR", { day:"2-digit", month:"2-digit", year:"numeric" }) : "";
const toInputDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : "";

// ── Componente principal ──────────────────────────────────────────────────────
export function ApprovalConfig({ onBack, siteId, siteName }) {
  const { accessToken, user } = useAuth();
  const { translations }      = useLanguage();
  const t = (key) => translations[key] || key;
  const canSelectSite = isMaster(user?.role) || isAdmin(user?.role);
  const { company: contextCompany } = useCompany();
  const hasContextCompany = contextCompany && contextCompany !== "0" && contextCompany !== 0;

  const [activeTab,    setActiveTab]    = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [allSites,     setAllSites]     = useState([]);
  const [configSiteId, setConfigSiteId] = useState(siteId || "");

  // Estado del formulario
  const [levels,       setLevels]       = useState([]);
  const [reglas,       setReglas]       = useState([]);
  const [escalamiento, setEscalamiento] = useState(null);
  const [delegaciones, setDelegaciones] = useState([]);
  const [updatedBy,    setUpdatedBy]    = useState("");
  const [updatedAt,    setUpdatedAt]    = useState(null);

  // Nivel headers (inside component to use t())
  const NIVEL_HEADERS = [
    { numero: 1, nombre: t("apc_level1_name"), descripcion: t("apc_level1_desc") },
    { numero: 2, nombre: t("apc_level2_name"), descripcion: t("apc_level2_desc") },
    { numero: 3, nombre: t("apc_level3_name"), descripcion: t("apc_level3_desc") },
    { numero: 4, nombre: t("apc_level4_name"), descripcion: t("apc_level4_desc") },
    { numero: 5, nombre: t("apc_level5_name"), descripcion: t("apc_level5_desc") },
  ];

  // Carga lista de sitios (solo para master/admin)
  useEffect(() => {
    if (!canSelectSite) return;
    (async () => {
      try {
        const sts = hasContextCompany
          ? await siteController.getSitesByCompany(accessToken, contextCompany, true)
          : await siteController.getSites(accessToken, true);
        setAllSites(sts.map(s => ({ value: s._id, label: s.name })));
      } catch { /* ignora */ }
    })();
  }, [accessToken, contextCompany]);

  // ── Carga config del sitio seleccionado ─────────────────────────────────────
  useEffect(() => {
    if (!configSiteId) return;
    (async () => {
      try {
        setLoading(true);
        const cfg = await api.getConfig(accessToken, configSiteId);
        setLevels(cfg.levels || []);
        setReglas(cfg.reglas || []);
        setEscalamiento(cfg.escalamiento || null);
        setDelegaciones(cfg.delegaciones || []);
        setUpdatedBy(cfg.updatedBy || "");
        setUpdatedAt(cfg.updatedAt || null);
      } catch { toast.error(t("apc_load_error")); }
      finally { setLoading(false); }
    })();
  }, [configSiteId]);

  // ── Guardar ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!configSiteId) return;
    setSaving(true);
    try {
      const saved = await api.upsertConfig(accessToken, configSiteId, { levels, reglas, escalamiento, delegaciones });
      setUpdatedBy(saved.updatedBy || "");
      setUpdatedAt(saved.updatedAt || null);
      toast.success(t("apc_saved_config"));
    } catch { toast.error(t("apc_save_error_config")); }
    finally { setSaving(false); }
  };

  // ── Helpers niveles ─────────────────────────────────────────────────────────
  const updateLevel = (idx, key, val) =>
    setLevels(prev => prev.map((l, i) => i === idx ? { ...l, [key]: val } : l));

  const toggleAccion = (idx, accion) =>
    setLevels(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = l.acciones.includes(accion)
        ? l.acciones.filter(a => a !== accion)
        : [...l.acciones, accion];
      return { ...l, acciones: next };
    }));

  const addTitular  = (idx) => setLevels(prev => prev.map((l, i) => i !== idx ? l : { ...l, titulares: [...l.titulares, emptyTitular()] }));
  const addSuplente = (idx) => setLevels(prev => prev.map((l, i) => i !== idx ? l : { ...l, suplentes: [...l.suplentes, emptySuplente()] }));

  const updateTitular  = (li, ti, key, val) => setLevels(prev => prev.map((l, i) => i !== li ? l : { ...l, titulares: l.titulares.map((t, j) => j !== ti ? t : { ...t, [key]: val }) }));
  const updateSuplente = (li, si, key, val) => setLevels(prev => prev.map((l, i) => i !== li ? l : { ...l, suplentes: l.suplentes.map((s, j) => j !== si ? s : { ...s, [key]: val }) }));
  const removeTitular  = (li, ti) => setLevels(prev => prev.map((l, i) => i !== li ? l : { ...l, titulares: l.titulares.filter((_, j) => j !== ti) }));
  const removeSuplente = (li, si) => setLevels(prev => prev.map((l, i) => i !== li ? l : { ...l, suplentes: l.suplentes.filter((_, j) => j !== si) }));

  // ── Helpers reglas ──────────────────────────────────────────────────────────
  const updateRegla = (idx, key, val) => setReglas(prev => prev.map((r, i) => i !== idx ? r : { ...r, [key]: val }));
  const removeRegla = (idx) => setReglas(prev => prev.filter((_, i) => i !== idx));

  // ── Helpers escalamiento ────────────────────────────────────────────────────
  const updateEsc = (key, val) => setEscalamiento(prev => ({ ...prev, [key]: val }));
  const updateCanal = (key, val) => setEscalamiento(prev => ({ ...prev, canales: { ...prev.canales, [key]: val } }));
  const updateSuspAuto = (key, val) => setEscalamiento(prev => ({ ...prev, suspensionAutomatica: { ...prev.suspensionAutomatica, [key]: val } }));

  // ── Helpers delegaciones ────────────────────────────────────────────────────
  const updateDeleg = (idx, key, val) => setDelegaciones(prev => prev.map((d, i) => i !== idx ? d : { ...d, [key]: val }));
  const removeDeleg = (idx) => setDelegaciones(prev => prev.filter((_, i) => i !== idx));

  // ── Render ──────────────────────────────────────────────────────────────────
  const fmtUpdated = updatedAt
    ? `${fmtDate(updatedAt)} ${new Date(updatedAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} · ${updatedBy}`
    : null;

  const tabs = [
    { label: t("apc_tab_approvers"),   icon: "users"    },
    { label: t("apc_tab_rules"),       icon: "fork"     },
    { label: t("apc_tab_escalation"),  icon: "bell"     },
    { label: t("apc_tab_delegations"), icon: "exchange" },
  ];

  return (
    <div className="apc">

      {/* ── Cabecera ── */}
      <div className="apc__header">
        <div className="apc__header-left">
          <div className="apc__title">{t("apc_title")}</div>
          <div className="apc__subtitle">{t("apc_subtitle")}</div>
        </div>
        <div className="apc__header-right">
          {canSelectSite ? (
            <div className="apc__sitio-box apc__sitio-box--select">
              <span className="apc__sitio-label">{t("apc_site_label")}</span>
              <select
                className="apc__sitio-select"
                value={configSiteId}
                onChange={e => setConfigSiteId(e.target.value)}
              >
                <option value="">{t("apc_select_site")}</option>
                {allSites.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="apc__sitio-box">
              <span className="apc__sitio-label">{t("apc_site_label")}</span>
              <span className="apc__sitio-name">
                {allSites.find(s => s.value === configSiteId)?.label || siteName || "—"}
              </span>
            </div>
          )}
          <button className="apc__close-btn" onClick={onBack} type="button">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="apc__tabs">
        {tabs.map((tb, i) => (
          <button
            key={i}
            className={`apc__tab${activeTab === i ? " apc__tab--active" : ""}`}
            onClick={() => setActiveTab(i)}
          >
            <Icon name={tb.icon} />
            {tb.label}
          </button>
        ))}
      </div>

      {/* ── Contenido ── */}
      {loading ? (
        <div className="apc__loading">{t("apc_loading")}</div>
      ) : (
        <div className="apc__body">

          {/* ══ Tab 1: Aprobadores por nivel ══ */}
          {activeTab === 0 && (
            <div className="apc__tab-content">
              <div className="apc__info-box">
                <Icon name="info circle" />
                <span>
                  <strong>{t("apc_how_it_works")}:</strong>{t("apc_how_desc1")}
                  <strong>{t("apc_titular")}</strong>{t("apc_how_desc2")}
                  <strong>{t("apc_suplentes_word")}</strong>{t("apc_how_desc3")}
                  <strong>{t("apc_criterio_asignacion")}</strong>{t("apc_how_desc4")}
                </span>
              </div>

              {NIVEL_HEADERS.map((nh, idx) => {
                const lv = levels[idx] || {};
                return (
                  <div key={idx} className="apc__nivel-card">
                    <div className="apc__nivel-header">
                      <span className="apc__nivel-num">{nh.numero}</span>
                      <div>
                        <div className="apc__nivel-nombre">{nh.nombre}</div>
                        <div className="apc__nivel-desc">{nh.descripcion}</div>
                      </div>
                      <div className="apc__nivel-activo">
                        <input type="checkbox" checked disabled readOnly />
                        <span>{t("apc_active_required")}</span>
                      </div>
                    </div>

                    <div className="apc__nivel-body">
                      {/* Criterio + Rol + Acciones */}
                      <div className="apc__nivel-row1">
                        <div className="apc__field">
                          <label className="apc__field-label">{t("apc_criterio_label")}</label>
                          <select
                            className="apc__select"
                            value={lv.criterio || "rol"}
                            onChange={e => updateLevel(idx, "criterio", e.target.value)}
                            disabled={nh.numero === 1}
                          >
                            {CRITERIO_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.tkey)}</option>)}
                          </select>
                          {lv.criterio === "rol" && (
                            <span className="apc__field-hint">{t("apc_criterio_rol_hint")}</span>
                          )}
                        </div>

                        {lv.criterio === "rol" && nh.numero > 1 && (
                          <div className="apc__field">
                            <label className="apc__field-label">{t("apc_rol_label")}</label>
                            <select
                              className="apc__select"
                              value={lv.rol || ""}
                              onChange={e => updateLevel(idx, "rol", e.target.value)}
                            >
                              <option value="">{t("apc_select_rol")}</option>
                              {ROL_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                        )}

                        <div className="apc__field apc__field--acciones">
                          <label className="apc__field-label">{t("apc_acciones_label")}</label>
                          <div className="apc__acciones">
                            {ACCIONES.map(a => (
                              <label key={a.key} className="apc__check-label">
                                <input
                                  type="checkbox"
                                  checked={lv.acciones?.includes(a.key) || false}
                                  onChange={() => toggleAccion(idx, a.key)}
                                />
                                {t("flujo_accion_" + a.key)}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Titulares */}
                      {nh.numero > 1 && (
                        <>
                          <div className="apc__sub-label">{t("apc_titulares_label")}</div>
                          <div className="apc__personas-grid">
                            {(lv.titulares || []).map((tit, ti) => (
                              <div key={ti} className="apc__persona-row">
                                <input className="apc__input" placeholder={t("name")} value={tit.nombre} onChange={e => updateTitular(idx, ti, "nombre", e.target.value)} />
                                <input className="apc__input" placeholder="Email" value={tit.email} onChange={e => updateTitular(idx, ti, "email", e.target.value)} />
                                <input className="apc__input" placeholder={t("apc_cargo")} value={tit.cargo} onChange={e => updateTitular(idx, ti, "cargo", e.target.value)} />
                                <label className="apc__firma-check">
                                  <input type="checkbox" checked={tit.firmaDigital} onChange={e => updateTitular(idx, ti, "firmaDigital", e.target.checked)} />
                                  <span className="apc__firma-badge"><Icon name="shield" /> {t("apc_firma")}</span>
                                </label>
                                <button className="apc__icon-btn apc__icon-btn--danger" onClick={() => removeTitular(idx, ti)}><Icon name="trash alternate outline" /></button>
                              </div>
                            ))}
                            <button className="apc__add-btn" onClick={() => addTitular(idx)}>
                              <Icon name="user plus" /> {t("apc_add_titular")}
                            </button>
                          </div>
                          <p className="apc__field-hint">{t("apc_titulares_hint")}</p>

                          {/* Suplentes */}
                          <div className="apc__sub-label">{t("apc_suplentes_label")}</div>
                          <div className="apc__personas-grid">
                            {(lv.suplentes || []).map((s, si) => (
                              <div key={si} className="apc__persona-row">
                                <input className="apc__input" placeholder={t("name")} value={s.nombre} onChange={e => updateSuplente(idx, si, "nombre", e.target.value)} />
                                <input className="apc__input" placeholder="Email" value={s.email} onChange={e => updateSuplente(idx, si, "email", e.target.value)} />
                                <input className="apc__input" placeholder={t("apc_cargo")} value={s.cargo} onChange={e => updateSuplente(idx, si, "cargo", e.target.value)} />
                                <button className="apc__icon-btn apc__icon-btn--danger" onClick={() => removeSuplente(idx, si)}><Icon name="trash alternate outline" /></button>
                              </div>
                            ))}
                            <button className="apc__add-btn" onClick={() => addSuplente(idx)}>
                              <Icon name="user plus" /> {t("apc_add_suplente")}
                            </button>
                          </div>
                          <p className="apc__field-hint">{t("apc_suplentes_hint")}</p>
                        </>
                      )}

                      {/* Firma + Comentario */}
                      <div className="apc__nivel-checks">
                        <label className="apc__check-label apc__check-label--blue">
                          <input type="checkbox" checked={lv.firmaDigitalObligatoria || false} onChange={e => updateLevel(idx, "firmaDigitalObligatoria", e.target.checked)} />
                          {t("apc_firma_digital")}
                        </label>
                        <label className="apc__check-label apc__check-label--blue">
                          <input type="checkbox" checked={lv.comentarioObligatorio || false} onChange={e => updateLevel(idx, "comentarioObligatorio", e.target.checked)} />
                          {t("apc_comentario_obligatorio")}
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ══ Tab 2: Reglas por tipo de permiso ══ */}
          {activeTab === 1 && (
            <div className="apc__tab-content">
              <div className="apc__info-box">
                <Icon name="info circle" />
                <span>
                  <strong>{t("apc_rules_title")}:</strong>{t("apc_rules_desc")}
                </span>
              </div>

              <table className="apc__reglas-table">
                <thead>
                  <tr>
                    <th>{t("apc_cond_label")}</th>
                    <th>{t("apc_action_label")}</th>
                    <th>{t("apc_additional_approver")}</th>
                    <th>{t("apc_active_label")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <select className="apc__select apc__select--sm" value={r.condicion} onChange={e => updateRegla(i, "condicion", e.target.value)}>
                          <option value="">{t("apc_select_condition")}</option>
                          {CONDICION_OPTIONS.map(c => <option key={c.value} value={c.value}>{t(c.tkey)}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="apc__select apc__select--sm" value={r.accion} onChange={e => updateRegla(i, "accion", e.target.value)}>
                          {ACCION_REGLA_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.tkey)}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="apc__input" placeholder={t("apc_approver_ph")} value={r.aprobadorAdicional} onChange={e => updateRegla(i, "aprobadorAdicional", e.target.value)} />
                      </td>
                      <td>
                        <label className="apc__toggle">
                          <input type="checkbox" checked={r.activa} onChange={e => updateRegla(i, "activa", e.target.checked)} />
                          <span className="apc__toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <button className="apc__icon-btn apc__icon-btn--danger" onClick={() => removeRegla(i)}>
                          <Icon name="trash alternate outline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button className="apc__add-btn apc__add-btn--outline" onClick={() => setReglas(prev => [...prev, emptyRegla()])}>
                <Icon name="plus" /> {t("apc_add_rule")}
              </button>
            </div>
          )}

          {/* ══ Tab 3: Escalamiento y alertas ══ */}
          {activeTab === 2 && escalamiento && (
            <div className="apc__tab-content">
              {/* Timeouts */}
              <div className="apc__esc-card">
                <div className="apc__esc-row4">
                  <div className="apc__field">
                    <label className="apc__field-label">{t("apc_timeout_label")}</label>
                    <input type="number" min={1} className="apc__input" value={escalamiento.timeoutMinutos} onChange={e => updateEsc("timeoutMinutos", Number(e.target.value))} />
                    <span className="apc__field-hint">{t("apc_timeout_hint")}</span>
                  </div>
                  <div className="apc__field">
                    <label className="apc__field-label">{t("apc_reminder_label")}</label>
                    <input type="number" min={1} className="apc__input" value={escalamiento.recordatorioMinutos} onChange={e => updateEsc("recordatorioMinutos", Number(e.target.value))} />
                    <span className="apc__field-hint">{t("apc_reminder_hint")}</span>
                  </div>
                  <div className="apc__field">
                    <label className="apc__field-label">{t("apc_escalate_to_label")}</label>
                    <select className="apc__select" value={escalamiento.escalarA} onChange={e => updateEsc("escalarA", e.target.value)}>
                      {ESCALAR_A_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.tkey)}</option>)}
                    </select>
                  </div>
                  <div className="apc__field">
                    <label className="apc__field-label">{t("apc_action_expire_label")}</label>
                    <select className="apc__select" value={escalamiento.accionAlVencer} onChange={e => updateEsc("accionAlVencer", e.target.value)}>
                      {ACCION_VENCER_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.tkey)}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Canales */}
              <div className="apc__esc-card">
                <h3 className="apc__esc-card-title">{t("apc_channels_title")}</h3>
                <div className="apc__canales-grid">
                  {[
                    { key: "email",            label: "Email" },
                    { key: "pushInApp",        label: "Push in-app" },
                    { key: "sms",              label: "SMS" },
                    { key: "slackTeams",       label: "Slack / Teams" },
                    { key: "whatsappBusiness", label: "WhatsApp Business" },
                  ].map(c => (
                    <label key={c.key} className="apc__canal-item">
                      <input type="checkbox" checked={escalamiento.canales?.[c.key] || false} onChange={e => updateCanal(c.key, e.target.checked)} />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Suspensión automática */}
              <div className="apc__esc-card">
                <h3 className="apc__esc-card-title">{t("apc_auto_suspend_title")}</h3>
                <div className="apc__suspension-grid">
                  {SUSP_AUTO_OPTIONS.map(s => (
                    <label key={s.key} className="apc__canal-item">
                      <input type="checkbox" checked={escalamiento.suspensionAutomatica?.[s.key] || false} onChange={e => updateSuspAuto(s.key, e.target.checked)} />
                      {t(s.tkey)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══ Tab 4: Delegaciones temporales ══ */}
          {activeTab === 3 && (
            <div className="apc__tab-content">
              <div className="apc__info-box">
                <Icon name="info circle" />
                <span>
                  <strong>{t("apc_delegations_title")}:</strong>{t("apc_delegations_desc")}
                </span>
              </div>

              <table className="apc__deleg-table">
                <thead>
                  <tr>
                    <th>{t("apc_delegates")}</th>
                    <th>{t("apc_in_favor_of")}</th>
                    <th>{t("apc_levels_label")}</th>
                    <th>{t("apc_from")}</th>
                    <th>{t("apc_to_date")}</th>
                    <th>{t("apc_reason")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {delegaciones.map((d, i) => (
                    <tr key={i}>
                      <td><input className="apc__input" placeholder={t("name")} value={d.delegante} onChange={e => updateDeleg(i, "delegante", e.target.value)} /></td>
                      <td><input className="apc__input" placeholder={t("name")} value={d.delegado} onChange={e => updateDeleg(i, "delegado", e.target.value)} /></td>
                      <td>
                        <div className="apc__niveles-pills">
                          {[1,2,3,4,5].map(n => (
                            <label key={n} className={`apc__nivel-pill${d.niveles?.includes(n) ? " apc__nivel-pill--on" : ""}`}>
                              <input type="checkbox"
                                checked={d.niveles?.includes(n) || false}
                                onChange={e => {
                                  const next = e.target.checked
                                    ? [...(d.niveles || []), n].sort()
                                    : (d.niveles || []).filter(x => x !== n);
                                  updateDeleg(i, "niveles", next);
                                }}
                              />
                              N{n}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td><input type="date" className="apc__input" value={toInputDate(d.desde)} onChange={e => updateDeleg(i, "desde", e.target.value)} /></td>
                      <td><input type="date" className="apc__input" value={toInputDate(d.hasta)} onChange={e => updateDeleg(i, "hasta", e.target.value)} /></td>
                      <td><input className="apc__input" placeholder={t("apc_reason")} value={d.motivo} onChange={e => updateDeleg(i, "motivo", e.target.value)} /></td>
                      <td><button className="apc__icon-btn apc__icon-btn--danger" onClick={() => removeDeleg(i)}><Icon name="trash alternate outline" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button className="apc__add-btn apc__add-btn--outline" onClick={() => setDelegaciones(prev => [...prev, emptyDeleg()])}>
                <Icon name="plus" /> {t("apc_add_delegation")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Footer ── */}
      <div className="apc__footer">
        <span className="apc__footer-info">
          {fmtUpdated ? <><Icon name="file outline" />{t("apc_last_updated")} {fmtUpdated}</> : null}
        </span>
        <div className="apc__footer-btns">
          <button className="apc__btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <Icon name="spinner" loading /> : <Icon name="save" />}
            {t("apc_save_config")}
          </button>
          <button className="apc__btn-cancel" onClick={onBack}>{t("cancel")}</button>
        </div>
      </div>
    </div>
  );
}
